/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useLDCContext } from '../../contexts/LoanDefaultCodesContext';
import { ListItem, ListItemText, IconButton } from '@mui/material';
import {
  epochToPST8AMDate,
  formatMMDDYYYY,
  getTimestampAsDate,
  isDateBefore,
  isDateBeforeCurrentMonthUTC,
  isGettingDeleted,
  isReportingDateValid,
} from '../../common/UtilityFunctions';
import { Delete } from '@mui/icons-material';
import Styles from '../../css/LoanDefaultCodes.module.css';
import ddsOccurrenceDateRow from './DDSOccurrenceDateRow';
import { useLocation } from 'react-router-dom';

type ddsItemProps = {
  listElem: EventObj;
  investor: boolean;
};

function isDDSCodeDeletable(timestamp: number): boolean {
  if (!timestamp) return true;
  const date = epochToPST8AMDate(timestamp);
  const minMonth = new Date();
  minMonth.setDate(1);
  minMonth.setMonth(minMonth.getMonth() - 1);
  return !isDateBefore(date, minMonth);
}

const ddsCodeListItem = (props: ddsItemProps) => {
  const { LDCFunctions, LDCUIStatuses, LDCInfo } = useLDCContext();
  const { pathname } = useLocation();
  const { listElem, investor } = props;
  const { handleLDCChange, enqueueReportingDateErrors } = LDCFunctions;
  const { isEditable } = LDCUIStatuses;
  const { DDSDate, investorDDSDate } = LDCInfo;
  const nameStr = (investor ? 'investorDDSDate.' : 'DDSDate.') + listElem.occurance;
  const endpointToCheck = investor ? investorDDSDate : DDSDate;
  const dateToCheck = endpointToCheck[listElem.occurance] ? endpointToCheck[listElem.occurance] : null;
  const gettingDeleted = isGettingDeleted(dateToCheck);
  const editable =
    isEditable &&
    !isDateBeforeCurrentMonthUTC(listElem.timestamp) &&
    !gettingDeleted &&
    pathname.includes('loan-default-codes');
  const reportingDateEditable =
    listElem.reportingDate &&
    isEditable &&
    !isDateBeforeCurrentMonthUTC(listElem.reportingDate) &&
    !gettingDeleted &&
    pathname.includes('loan-default-codes');
  const deletable =
    isEditable &&
    !gettingDeleted &&
    isDDSCodeDeletable(listElem.reportingDate) &&
    pathname.includes('loan-default-codes');

  return (
    <tr>
      <td>
        {deletable && (
          <IconButton
            title={'Delete'}
            onClick={() => {
              handleLDCChange('edit', nameStr, { active: false, ddsOccurrence: listElem.occurance });
            }}
          >
            <Delete />
          </IconButton>
        )}
      </td>
      <td className={!isEditable || isDateBeforeCurrentMonthUTC(listElem.timestamp) ? Styles.disabled : ''}>
        <ListItem>
          <ListItemText
            style={{ textDecoration: gettingDeleted ? 'line-through' : '' }}
            primary={formatMMDDYYYY(listElem.date)}
            secondary={
              <>
                {editable &&
                  ddsOccurrenceDateRow(
                    'Change DDS Date to: ',
                    endpointToCheck[listElem.occurance]?.activeDate
                      ? endpointToCheck[listElem.occurance].activeDate
                      : '',
                    nameStr + '.activeDate',
                    !isEditable,
                    (e) => {
                      handleLDCChange('edit', nameStr + '.ddsOccurrence', listElem.occurance);
                    },
                  )}

                <div>{listElem.referenceCodeAbbreviation + ' - ' + listElem.referenceCodeDescription}</div>
                <div>{'Date created: ' + getTimestampAsDate(listElem.timestamp)}</div>
                {listElem.reportingDate !== null && (
                  <div>{'Reporting Date: ' + getTimestampAsDate(listElem.reportingDate)}</div>
                )}
                {reportingDateEditable &&
                  ddsOccurrenceDateRow(
                    'Change Reporting Date to: ',
                    endpointToCheck[listElem.occurance]?.reportingDate
                      ? endpointToCheck[listElem.occurance].reportingDate
                      : '',
                    nameStr + '.reportingDate',
                    !isEditable,
                    (e) => {
                      if (e.target.value !== null && e.target.value !== '' && !isReportingDateValid(e)) {
                        e.target.value = null;
                        handleLDCChange('cancel', nameStr + '.reportingDate');
                        enqueueReportingDateErrors('invalid');
                      } else {
                        handleLDCChange('edit', nameStr + '.ddsOccurrence', listElem.occurance);
                      }
                    },
                  )}
              </>
            }
          />
        </ListItem>
      </td>
    </tr>
  );
};

export default ddsCodeListItem;
